import { useState, useRef, useEffect, useCallback } from 'react';
import { Icon } from './ui/Icon.js';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  'data-testid'?: string;
  /** Put on the trigger, so a visible `<label htmlFor>` names it (spec 249). */
  id?: string;
}

export function Select({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  className = '',
  'data-testid': testId,
  id,
}: SelectProps) {
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selectedOption = options.find((o) => o.value === value);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll highlighted option into view
  useEffect(() => {
    if (open && highlightedIndex >= 0 && listRef.current) {
      const items = listRef.current.children;
      if (items[highlightedIndex]) {
        (items[highlightedIndex] as HTMLElement).scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex, open]);

  const handleSelect = useCallback(
    (optionValue: string) => {
      onChange(optionValue);
      setOpen(false);
    },
    [onChange],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'Enter':
        case ' ': {
          e.preventDefault();
          if (!open) {
            setOpen(true);
            const currentIdx = options.findIndex((o) => o.value === value);
            setHighlightedIndex(currentIdx >= 0 ? currentIdx : 0);
          } else if (highlightedIndex >= 0 && highlightedIndex < options.length) {
            handleSelect(options[highlightedIndex].value);
          }
          break;
        }
        case 'ArrowDown': {
          e.preventDefault();
          if (!open) {
            setOpen(true);
            const currentIdx = options.findIndex((o) => o.value === value);
            setHighlightedIndex(currentIdx >= 0 ? currentIdx : 0);
          } else {
            setHighlightedIndex((prev) => (prev < options.length - 1 ? prev + 1 : 0));
          }
          break;
        }
        case 'ArrowUp': {
          e.preventDefault();
          if (open) {
            setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : options.length - 1));
          }
          break;
        }
        case 'Escape': {
          e.preventDefault();
          setOpen(false);
          break;
        }
        case 'Tab': {
          setOpen(false);
          break;
        }
      }
    },
    [open, highlightedIndex, options, value, handleSelect],
  );

  return (
    <div ref={containerRef} className={`relative ${className}`} data-testid={testId}>
      <button
        id={id}
        type="button"
        onClick={() => {
          setOpen((prev) => {
            if (!prev) {
              const currentIdx = options.findIndex((o) => o.value === value);
              setHighlightedIndex(currentIdx >= 0 ? currentIdx : 0);
            }
            return !prev;
          });
        }}
        onKeyDown={handleKeyDown}
        className="hub-select flex w-full items-center justify-between text-left transition-colors focus-visible:outline-none"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className={selectedOption ? '' : 'text-[var(--hub-color-ink-subtle)]'}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <Icon name="bx-chevron-down" className={`ml-2 flex-shrink-0 text-[var(--hub-select-arrow)] transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <ul
          ref={listRef}
          role="listbox"
          className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-[var(--hub-radius-control)] border border-[var(--hub-color-line)] bg-[var(--hub-color-surface)] py-1"
        >
          {options.map((option, idx) => {
            const isActive = option.value === value;
            const isHighlighted = idx === highlightedIndex;
            return (
              <li
                key={option.value}
                role="option"
                aria-selected={isActive}
                className={`cursor-pointer px-3 py-1.5 text-sm transition-colors ${
                  isActive
                    ? 'bg-[var(--hub-color-action-soft)] font-medium text-[var(--hub-color-action-text)]'
                    : isHighlighted
                      ? 'bg-[var(--hub-color-surface-sunken)] text-[var(--hub-color-ink)]'
                      : 'text-[var(--hub-color-ink)] hover:bg-[var(--hub-color-surface-sunken)]'
                }`}
                onClick={() => handleSelect(option.value)}
                onMouseEnter={() => setHighlightedIndex(idx)}
              >
                {option.label}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
